// Translations for the PaperBrainz landing page.
// Armenian translations: VERIFY before production — written by Claude, should be reviewed.
// Tab title format: page joins with ` - ` per SEO convention.

const en = {
  // Nav
  "nav.howItWorks": "How It Works",
  "nav.features": "Features",
  "nav.pricing": "Pricing",
  "nav.faq": "FAQ",
  "nav.integrations": "Integrations",
  "nav.chatbotFor": "Chatbot for",
  "nav.login": "Login",
  "nav.startFreeTrial": "Start free trial",
  "nav.startFreeTrialArrow": "Start free trial →",
  "nav.dashboard": "Dashboard →",

  // SEO
  "seo.title": "PaperBrainz - AI Chatbot for Customer Support",
  "seo.description": "AI chatbot trained on your docs. Automate customer support on your website 24/7. PDFs, FAQs, knowledge base. One-line install. 14-day free trial — no credit card. From $7.99/mo.",

  // Hero
  "hero.eyebrow": "PaperBrainz for small businesses",
  "hero.title1": "Stop losing customers",
  "hero.title2": "to slow replies.",
  "hero.subtitle": "AI customer support for your website. Trained on your business automatically. Answers every visitor, 24/7.",
  "hero.ctaPrimary": "Start free trial",
  "hero.ctaPrimaryLoggedIn": "Go to Dashboard",
  "hero.ctaSecondary": "Ask our chatbot",
  "hero.note": "14 days free · No credit card required",
  "hero.imageAlt": "PaperBrainz chat widget answering customer questions",

  // Trust bar
  "trust.label": "Built with enterprise-grade infrastructure",
  "trust.supabase": "Supabase",
  "trust.qdrant": "Qdrant Vector DB",
  "trust.ssl": "SSL Secured",
  "trust.uptime": "99.9% Uptime Target",

  // Stats
  "stats.eyebrow": "The shift",
  "stats.title1": "Customers won't wait.",
  "stats.title2": "Your team can't be everywhere.",
  "stats.subtitle": "The questions your visitors ask most are the ones you answer most. PaperBrainz handles them automatically — so people get answers instantly, and your team stays focused on the work that matters.",
  // The figures rendered next to these lines (24/7, 28, 1) are product facts, not
  // market statistics. Keep them verifiable: 28 = languages listed in faq.q7.a.
  "stats.s1.title": "answering, no hiring required",
  "stats.s1.desc": "No breaks, no holidays, no sick days. Every visitor gets a reply, even at 3 AM.",
  // 28 = languages in backend/locales/languageDefaults.js. This covers the widget's
  // own text (greeting, welcome title, placeholder, suggested questions), which the
  // owner picks in settings. It is NOT a claim about what language the AI answers in
  // — answer generation lives in the external ML service and isn't verifiable here.
  "stats.s2.title": "languages for your widget",
  "stats.s2.desc": "Greeting, placeholder and suggested questions ready to go in 28 languages — pick yours in settings.",
  "stats.s3.title": "line of code to go live",
  "stats.s3.desc": "One script tag on your site. No developer, no plugin, no rebuild.",

  // How it works
  "how.title": "Three steps to go live",
  "how.subtitle": "Set up your AI chatbot and add it to your website",
  "how.s1.title": "Add your content",
  "how.s1.desc": "Upload PDFs, paste text, or scrape your website. Your AI learns automatically — no training data needed.",
  "how.s2.title": "Match your brand",
  "how.s2.desc": "Pick colors, logo, position, and greeting. Light or dark mode. Live preview as you go.",
  "how.s3.title": "Paste one line",
  "how.s3.desc": "Copy a single script tag into your site. Your chatbot answers customers — 24/7, in any language.",

  // What visitors see
  "visitor.title": "What your customers see",
  "visitor.subtitle": "They arrive with a question and leave with an answer — no form, no waiting for opening hours.",
  "visitor.v1.kicker": "Home",
  "visitor.v1.title": "They start with a question",
  "visitor.v1.desc": "The questions you get asked most, one tap away. Plus WhatsApp, email or phone for anyone who would rather reach a person.",
  "visitor.v1.imageAlt": "The PaperBrainz widget home screen on a small business website, showing tappable questions and contact options",
  "visitor.v2.kicker": "Chat",
  "visitor.v2.title": "They get an answer",
  "visitor.v2.desc": "Trained on your own pages, PDFs and FAQs. It replies in seconds, at any hour, in your colors and your logo.",
  "visitor.v2.imageAlt": "The PaperBrainz widget answering a customer question about an order on a small business website",

  // Features
  "features.title": "Everything you need. Nothing you don't.",
  "features.subtitle": "Train it, customize it, embed it — all in one dashboard.",
  "features.f1.title": "Train it on your content in minutes",
  "features.f1.desc": "Upload PDFs, paste in your FAQs, or just give it your website URL — it learns automatically. No training data, no fine-tuning, no setup wizard.",
  "features.f2.title": "Make it look like part of your site",
  "features.f2.desc": "Pick colors, position, logo, and greeting. Light or dark mode. Live preview as you adjust. The widget looks like you — not a third-party chatbot bolted on.",
  "features.f3.title": "See what customers really ask",
  "features.f3.desc": "Every conversation logged. Top questions ranked. Trends over time. Export transcripts anytime — and find out what your website is missing.",
  "features.f4.title": "Go live in 5 minutes, no developer needed",
  "features.f4.desc": "Copy one line of code. Paste it on your site. Done. Works on Shopify, Webflow, WordPress, or any custom HTML.",
  "features.mockNote": "Product interface shown is illustrative.",

  // Dashboard showcase
  "showcase.title": "One dashboard. Full control.",
  "showcase.subtitle": "Train your AI, customize the widget, see what customers ask — all in one place.",
  "showcase.imageAlt": "PaperBrainz dashboard overview with metrics and analytics",

  // Price comparison
  "compare.title1": "The real cost of",
  "compare.title2": "customer support.",
  "compare.subtitle": "Hiring is expensive. Missing customers is worse. PaperBrainz is neither — instant replies, every visitor, 24/7, for less than the price of a single team lunch.",
  "compare.eyebrow": "Monthly cost · same 24/7 coverage",
  "compare.note": "Typical fully-loaded compensation for a U.S. support hire, vs. PaperBrainz.",
  "compare.opt1.label": "One support agent",
  "compare.opt1.sub": "8 hours/day, weekdays only",
  "compare.opt2.label": "24/7 in-house team",
  "compare.opt2.sub": "3 shifts, nights, weekends, holidays",
  "compare.opt3.label": "PaperBrainz",
  "compare.opt3.sub": "Instant replies, every visitor, 24/7",
  "compare.permo": "/mo",
  "compare.footnote": "Illustrative estimate, not a quote. Agent cost based on the U.S. Bureau of Labor Statistics median customer-service representative wage (~$18/hr × 160 hrs ≈ $2,880/mo, before benefits & overhead). The 24/7 figure assumes roughly 4 full-time staff across shifts. Your own costs will differ.",

  // Pricing
  "pricing.title": "Pricing that makes sense",
  "pricing.subtitle": "Try Basic free for 14 days — no credit card required. Switch plans anytime from your dashboard.",
  "pricing.monthly": "Monthly",
  "pricing.annual": "Annual",
  "pricing.save": "Save 17%",
  "pricing.popular": "Most Popular",
  "pricing.perMonth": "/month",
  "pricing.billedAnnually": "billed annually",
  "pricing.starter.name": "Starter",
  "pricing.starter.desc": "For small sites just getting started",
  "pricing.starter.cta": "Get Starter",
  "pricing.starter.f1": "500 conversations/month",
  "pricing.starter.f2": "Up to 3 knowledge sources",
  "pricing.starter.f3": "25,000 character limit",
  "pricing.starter.f4": "Custom logo, colors & widget",
  "pricing.starter.f5": "Light & dark mode",
  "pricing.starter.f6": "Email support",
  "pricing.basic.name": "Basic",
  "pricing.basic.desc": "Most businesses choose this",
  "pricing.basic.cta": "Start 14-day free trial",
  "pricing.basic.f1": "2,000 conversations/month",
  "pricing.basic.f2": "Up to 10 knowledge sources",
  "pricing.basic.f3": "100,000 character limit",
  "pricing.basic.f4": "Conversation analytics",
  "pricing.basic.f5": "Transcript export (CSV)",
  "pricing.basic.f6": "Priority email support",
  "pricing.basic.f7": "Custom logo, colors & branding",
  "pricing.pro.name": "Pro",
  "pricing.pro.desc": "For high-traffic websites",
  "pricing.pro.cta": "Get Pro",
  "pricing.pro.f1": "8,000 conversations/month",
  "pricing.pro.f2": "Unlimited knowledge sources",
  "pricing.pro.f3": "200,000 character limit",
  "pricing.pro.f4": "Remove PaperBrainz branding",
  "pricing.pro.f5": "Advanced analytics (top questions, trends)",
  "pricing.pro.f6": "Priority chat support",
  "pricing.pro.f7": "Priority onboarding support",
  "pricing.pro.footnote": "Already using Basic? Upgrade anytime.",
  "pricing.bullet1": "14-day free trial on Basic · no credit card",
  "pricing.bullet2": "Switch plans anytime",
  "pricing.bullet3": "Cancel from your dashboard",
  "pricing.bullet4": "Widget text in 28 languages",

  // FAQ
  "faq.title": "Common questions",
  "faq.q1.q": "How does the 14-day free trial work?",
  "faq.q1.a": "Sign up and you get a 14-day free trial of the Basic plan — no credit card required to start. Your chatbot embeds on your site immediately with full Basic features during the trial. Add a payment method any time from your dashboard to keep your bot live after day 14. Cancel before then and nothing is charged. Starter and Pro are direct-purchase: if you want to test first, start with the trial and switch plans from your dashboard.",
  "faq.q2.q": "What counts as a conversation?",
  "faq.q2.a": "A conversation is a single chat session with one customer. It ends after 30 minutes of inactivity. So one customer asking 5 questions in a row counts as one conversation.",
  "faq.q3.q": "What if the AI doesn't know an answer?",
  "faq.q3.a": "When the chatbot cannot find relevant information in your content, it lets the user know and can suggest contacting your support team directly. You can customize the fallback message.",
  "faq.q4.q": "What can I train it on?",
  "faq.q4.a": "PDF, DOC, DOCX, TXT, and Markdown files. You can also paste in text, add Q&A pairs manually, or scrape content directly from your existing website.",
  "faq.q5.q": "How long does setup take?",
  "faq.q5.a": "Most users go live the same day. Upload your content, customize the widget, copy a single line of code into your site, and it's running.",
  "faq.q6.q": "Can I see what customers are asking?",
  "faq.q6.a": "Yes — every plan shows full conversation history. Basic and Pro add analytics, popular question reports, and transcript export.",
  "faq.q7.q": "What languages does it support?",
  // Two separate facts, kept separate on purpose. The 28 is the widget-string count in
  // backend/locales/languageDefaults.js. Conversation language is handled by the external
  // ML service: tested Aug 2026 — it answered a one-word "hola" in English, and switched
  // to Spanish only when asked. Do not restore "speaks 28 languages" phrasing.
  "faq.q7.a": "The widget's own text — greeting, placeholder and suggested questions — comes ready in 28 languages: English, Spanish, French, German, Italian, Portuguese, Dutch, Polish, Czech, Hungarian, Romanian, Greek, Russian, Ukrainian, Turkish, Arabic, Hebrew, Hindi, Thai, Vietnamese, Chinese, Japanese, Korean, Armenian, Danish, Finnish, Norwegian, and Swedish. You pick yours in settings. Conversations aren't limited to that list — the chatbot can reply in other languages too. It won't always switch on its own from a single word, but asking it to answer in a particular language works. The dashboard interface is in English.",
  "faq.q8.q": "Can I cancel anytime?",
  "faq.q8.a": "Yes. No contracts, no cancellation fees. Cancel directly from your dashboard whenever you want.",

  // CTA
  "cta.title1": "Stop losing customers.",
  "cta.title2": "Start with 14 days free.",
  "cta.subtitle": "No credit card required. Plans from $7.99/month.",
  "cta.primary": "Start free trial",
  "cta.primaryLoggedIn": "Go to Dashboard",
  "cta.secondary": "Compare plans",

  // Footer
  "footer.tagline": "AI-powered customer support for modern businesses.",
  "footer.product": "Product",
  "footer.integrations": "Integrations",
  "footer.legal": "Legal",
  "footer.account": "Account",
  "footer.security": "Security",
  "footer.privacy": "Privacy Policy",
  "footer.terms": "Terms of Service",
  "footer.refund": "Refund Policy",
  "footer.cookie": "Cookie Policy",
  "footer.login": "Login",
  "footer.signup": "Sign Up",
  "footer.dashboardLink": "Dashboard",
  "footer.support": "Support",
  "footer.rights": "All rights reserved.",
  "footer.ctaText": "Try PaperBrainz free for {days} days — no credit card. Plans from ${price}/month.",

  // Language switcher
  "lang.en": "EN",
  "lang.hy": "ՀԱՅ",
};

const hy = {
  // Nav
  "nav.howItWorks": "Ինչպես է աշխատում",
  "nav.features": "Հնարավորություններ",
  "nav.pricing": "Գներ",
  "nav.faq": "ՀՏՀ",
  "nav.integrations": "Ինտեգրումներ",
  "nav.chatbotFor": "Չատբոթ",
  "nav.login": "Մուտք",
  "nav.startFreeTrial": "Փորձել անվճար",
  "nav.startFreeTrialArrow": "Փորձել անվճար →",
  "nav.dashboard": "Վահանակ →",

  // SEO
  "seo.title": "PaperBrainz — AI չատբոթ հաճախորդների սպասարկման համար",
  "seo.description": "AI չատբոթ՝ սովորեցված ձեր նյութերով։ Ավտոմատ սպասարկում կայքում՝ 24/7։ PDF, FAQ, գիտելիքների բազա։ Մեկ տողով տեղադրում։ 14 օր անվճար՝ առանց բանկային քարտի։ Փաթեթներ՝ $7.99/ամսից։",

  // Hero
  "hero.eyebrow": "PaperBrainz փոքր բիզնեսների համար",
  "hero.title1": "Մի կորցրեք հաճախորդներ",
  "hero.title2": "դանդաղ պատասխանների պատճառով։",
  "hero.subtitle": "AI սպասարկում ձեր կայքի համար։ Սովորում է ձեր բիզնեսից և պատասխանում յուրաքանչյուր այցելուի՝ 24/7։",
  "hero.ctaPrimary": "Փորձել անվճար",
  "hero.ctaPrimaryLoggedIn": "Բացել վահանակը",
  "hero.ctaSecondary": "Հարցրեք մեր չատբոթին",
  "hero.note": "14 օր անվճար · առանց բանկային քարտի",
  "hero.imageAlt": "PaperBrainz չատ-վիջեթը պատասխանում է հաճախորդների հարցերին",

  // Trust bar
  "trust.label": "Կառուցված է բիզնես մակարդակի ենթակառուցվածքով",
  "trust.supabase": "Supabase",
  "trust.qdrant": "Qdrant Vector DB",
  "trust.ssl": "SSL պաշտպանություն",
  "trust.uptime": "99.9% հասանելիության թիրախ",

  // Stats
  "stats.eyebrow": "Փոփոխությունը",
  "stats.title1": "Հաճախորդները չեն սպասում։",
  "stats.title2": "Ձեր թիմը չի կարող ամենուր լինել։",
  "stats.subtitle": "Այցելուների ամենահաճախ տրվող հարցերն այն են, որոնց դուք ամենաշատն եք պատասխանում։ PaperBrainz-ը պատասխանում է դրանց ավտոմատ՝ հաճախորդներին տալով ակնթարթային պատասխան, իսկ ձեր թիմին՝ կենտրոնանալու կարևորի վրա։",
  "stats.s1.title": "պատասխանում է առանց նոր աշխատակցի",
  "stats.s1.desc": "Առանց ընդմիջման, արձակուրդի կամ հիվանդության։ Յուրաքանչյուր այցելու ստանում է պատասխան՝ նույնիսկ գիշերվա 3-ին։",
  "stats.s2.title": "լեզու ձեր վիջեթի համար",
  "stats.s2.desc": "Ողջույնի տեքստը, հուշումները և առաջարկվող հարցերը՝ պատրաստ 28 լեզվով։ Ընտրեք ձերը կարգավորումներում։",
  "stats.s3.title": "տող կոդ՝ գործարկելու համար",
  "stats.s3.desc": "Մեկ script տող ձեր կայքում։ Առանց ծրագրավորողի, առանց փլագինի։",

  // How it works
  "how.title": "Գործարկում՝ 3 քայլով",
  "how.subtitle": "Կարգավորեք AI չատբոթը և ավելացրեք այն ձեր կայքում",
  "how.s1.title": "Ավելացրեք բովանդակությունը",
  "how.s1.desc": "Վերբեռնեք PDF-ներ, տեղադրեք տեքստ կամ սկանավորեք կայքը։ AI-ն սովորում է ավտոմատ՝ առանց ուսուցման տվյալների։",
  "how.s2.title": "Հարմարեցրեք բրենդին",
  "how.s2.desc": "Ընտրեք գույներ, լոգո, դիրք և ողջույն։ Բաց կամ մուգ ռեժիմ՝ կենդանի նախադիտմամբ։",
  "how.s3.title": "Տեղադրեք մեկ տող",
  "how.s3.desc": "Պատճենեք մեկ script tag ձեր կայքում։ Չատբոթը կպատասխանի հաճախորդներին՝ 24/7, ցանկացած լեզվով։",

  // What visitors see
  "visitor.title": "Ինչ են տեսնում ձեր հաճախորդները",
  "visitor.subtitle": "Նրանք գալիս են հարցով և հեռանում պատասխանով՝ առանց ձևաթղթի, առանց աշխատանքային ժամեր սպասելու։",
  "visitor.v1.kicker": "Գլխավոր",
  "visitor.v1.title": "Սկսում են հարցից",
  "visitor.v1.desc": "Ամենահաճախ տրվող հարցերը՝ մեկ հպումով։ Նաև WhatsApp, էլ. փոստ կամ հեռախոս նրանց համար, ովքեր նախընտրում են խոսել մարդու հետ։",
  "visitor.v1.imageAlt": "PaperBrainz վիջեթի գլխավոր էկրանը փոքր բիզնեսի կայքում՝ հպվող հարցերով և կապի տարբերակներով",
  "visitor.v2.kicker": "Չատ",
  "visitor.v2.title": "Ստանում են պատասխան",
  "visitor.v2.desc": "Սովորած է ձեր իսկ էջերից, PDF-երից և հաճախ տրվող հարցերից։ Պատասխանում է վայրկյանների ընթացքում, ցանկացած ժամի՝ ձեր գույներով և ձեր լոգոյով։",
  "visitor.v2.imageAlt": "PaperBrainz վիջեթը պատասխանում է պատվերի մասին հաճախորդի հարցին փոքր բիզնեսի կայքում",

  // Features
  "features.title": "Ամեն ինչ, ինչ պետք է։ Ոչ ավելին։",
  "features.subtitle": "Սովորեցրեք, ձևավորեք և տեղադրեք՝ մեկ վահանակից։",
  "features.f1.title": "Սովորեցրեք ձեր նյութերով՝ րոպեների ընթացքում",
  "features.f1.desc": "Վերբեռնեք PDF-ներ, ավելացրեք FAQ կամ տվեք կայքի հասցեն․ AI-ն սովորում է ինքնուրույն։ Առանց fine-tuning-ի և բարդ կարգավորումների։",
  "features.f2.title": "Թող նման լինի ձեր կայքի մասի",
  "features.f2.desc": "Ընտրեք գույներ, դիրք, լոգո և ողջույն։ Բաց կամ մուգ ռեժիմ։ Վիջեթը նայում է ձեր բրենդի պես, ոչ թե կցված գործիքի։",
  "features.f3.title": "Տեսեք՝ ինչ են իրականում հարցնում հաճախորդները",
  "features.f3.desc": "Բոլոր զրույցները պահպանվում են։ Տեսեք գլխավոր հարցերը, միտումները և արտահանեք պատմությունը ցանկացած պահի։",
  "features.f4.title": "Գործարկեք 5 րոպեում՝ առանց ծրագրավորողի",
  "features.f4.desc": "Պատճենեք մեկ տող կոդ, տեղադրեք կայքում՝ և վերջ։ Աշխատում է Shopify, Webflow, WordPress և ցանկացած HTML կայքում։",
  "features.mockNote": "Ցուցադրված ինտերֆեյսը պատկերազարդ է։",

  // Dashboard showcase
  "showcase.title": "Մեկ վահանակ։ Լիակատար վերահսկում։",
  "showcase.subtitle": "Սովորեցրեք AI-ին, ձևավորեք վիջեթը և տեսեք հաճախորդների հարցերը՝ մեկ տեղում։",
  "showcase.imageAlt": "PaperBrainz վահանակ՝ ցուցանիշներով և վերլուծությամբ",

  // Price comparison
  "compare.title1": "Հաճախորդների սպասարկման",
  "compare.title2": "իրական արժեքը։",
  "compare.subtitle": "Աշխատակից վարձելը թանկ է։ Հաճախորդ կորցնելն՝ ավելի թանկ։ PaperBrainz-ը տալիս է ակնթարթային պատասխաններ յուրաքանչյուր այցելուի՝ 24/7, մեկ թիմային ճաշից էլ էժան։",
  "compare.eyebrow": "Ամսական արժեք · նույն 24/7 ծածկույթը",
  "compare.note": "ԱՄՆ-ում սպասարկման աշխատակցի միջին ամբողջական ծախսը՝ PaperBrainz-ի համեմատ։",
  "compare.opt1.label": "Մեկ սպասարկման աշխատակից",
  "compare.opt1.sub": "8 ժամ/օր, միայն աշխատանքային օրերին",
  "compare.opt2.label": "24/7 ներքին թիմ",
  "compare.opt2.sub": "3 հերթափոխ՝ գիշերներ, հանգստյան օրեր, տոներ",
  "compare.opt3.label": "PaperBrainz",
  "compare.opt3.sub": "Ակնթարթային պատասխաններ՝ յուրաքանչյուր այցելուի, 24/7",
  "compare.permo": "/ամիս",
  "compare.footnote": "Պատկերավոր հաշվարկ է, ոչ թե գնանշում։ Աշխատակցի ծախսը հիմնված է ԱՄՆ Աշխատանքի վիճակագրության բյուրոյի հաճախորդների սպասարկման ներկայացուցչի միջին վարձատրության վրա (~$18/ժամ × 160 ժամ ≈ $2,880/ամիս՝ առանց արտոնությունների և հարակից ծախսերի)։ 24/7 ցուցանիշը ենթադրում է մոտ 4 լրիվ դրույք հերթափոխերով։ Ձեր փաստացի ծախսերը կտարբերվեն։",

  // Pricing
  "pricing.title": "Գներ, որոնք տրամաբանական են",
  "pricing.subtitle": "Փորձեք Basic-ը անվճար 14 օր՝ առանց բանկային քարտի։ Փաթեթը կարող եք փոխել ցանկացած պահի վահանակից։",
  "pricing.monthly": "Ամսական",
  "pricing.annual": "Տարեկան",
  "pricing.save": "Խնայեք 17%",
  "pricing.popular": "Ամենապահանջվածը",
  "pricing.perMonth": "/ամիս",
  "pricing.billedAnnually": "գանձվում է տարեկան",
  "pricing.starter.name": "Starter",
  "pricing.starter.desc": "Փոքր կայքերի համար՝ մեկնարկի փուլում",
  "pricing.starter.cta": "Ընտրել Starter",
  "pricing.starter.f1": "500 զրույց/ամիս",
  "pricing.starter.f2": "Մինչև 3 գիտելիքի աղբյուր",
  "pricing.starter.f3": "25,000 նիշի սահման",
  "pricing.starter.f4": "Սեփական լոգո, գույներ և վիջեթ",
  "pricing.starter.f5": "Բաց և մուգ ռեժիմ",
  "pricing.starter.f6": "Էլ․ փոստի աջակցություն",
  "pricing.basic.name": "Basic",
  "pricing.basic.desc": "Բիզնեսների մեծ մասն ընտրում է սա",
  "pricing.basic.cta": "Փորձել 14 օր անվճար",
  "pricing.basic.f1": "2,000 զրույց/ամիս",
  "pricing.basic.f2": "Մինչև 10 գիտելիքի աղբյուր",
  "pricing.basic.f3": "100,000 նիշի սահման",
  "pricing.basic.f4": "Զրույցների վերլուծություն",
  "pricing.basic.f5": "Պատմության արտահանում (CSV)",
  "pricing.basic.f6": "Առաջնահերթ էլ․ փոստի աջակցություն",
  "pricing.basic.f7": "Սեփական լոգո, գույներ և բրենդավորում",
  "pricing.pro.name": "Pro",
  "pricing.pro.desc": "Բարձր այցելություն ունեցող կայքերի համար",
  "pricing.pro.cta": "Ընտրել Pro",
  "pricing.pro.f1": "8,000 զրույց/ամիս",
  "pricing.pro.f2": "Անսահմանափակ գիտելիքի աղբյուրներ",
  "pricing.pro.f3": "200,000 նիշի սահման",
  "pricing.pro.f4": "PaperBrainz բրենդինգի հեռացում",
  "pricing.pro.f5": "Ընդլայնված վերլուծություն՝ հարցեր և միտումներ",
  "pricing.pro.f6": "Առաջնահերթ չատ աջակցություն",
  "pricing.pro.f7": "Առաջնահերթ մեկնարկային աջակցություն",
  "pricing.pro.footnote": "Արդեն Basic-ում ե՞ք։ Թարմացրեք ցանկացած պահի։",
  "pricing.bullet1": "14 օր անվճար Basic-ի համար · առանց բանկային քարտի",
  "pricing.bullet2": "Փոխեք փաթեթը ցանկացած պահի",
  "pricing.bullet3": "Չեղարկեք վահանակից",
  "pricing.bullet4": "Վիջեթի տեքստը՝ 28 լեզվով",

  // FAQ
  "faq.title": "Հաճախ տրվող հարցեր",
  "faq.q1.q": "Ինչպե՞ս է աշխատում 14-օրյա անվճար փորձարկումը։",
  "faq.q1.a": "Գրանցվելուց հետո ստանում եք Basic փաթեթի 14-օրյա անվճար փորձարկում՝ առանց բանկային քարտի։ Չատբոթը անմիջապես տեղադրվում է ձեր կայքում Basic փաթեթի լիարժեք հնարավորություններով։ Ցանկացած պահի վահանակից կարող եք ավելացնել վճարման եղանակ՝ բոթը 14-րդ օրից հետո պահելու համար։ Մինչ այդ չեղարկելու դեպքում ոչինչ չի գանձվում։ Starter և Pro փաթեթները գնվում են ուղղակիորեն․ նախ փորձելու համար սկսեք փորձարկումից, հետո փոխեք փաթեթը վահանակից։",
  "faq.q2.q": "Ի՞նչն է համարվում զրույց։",
  "faq.q2.a": "Զրույցը մեկ հաճախորդի մեկ չատային սեսիան է։ Այն ավարտվում է 30 րոպե անգործությունից հետո։ Այսինքն՝ նույն հաճախորդի 5 անընդմեջ հարցը հաշվվում է որպես մեկ զրույց։",
  "faq.q3.q": "Ի՞նչ է լինում, եթե AI-ն պատասխանը չգիտի։",
  "faq.q3.a": "Եթե չատբոթը ձեր նյութերում համապատասխան տեղեկություն չի գտնում, նա տեղեկացնում է օգտատիրոջը և կարող է առաջարկել կապվել ձեր թիմի հետ։ Այդ fallback հաղորդագրությունը կարող եք հարմարեցնել։",
  "faq.q4.q": "Ինչի՞ վրա կարող եմ սովորեցնել չատբոթը։",
  "faq.q4.a": "PDF, DOC, DOCX, TXT և Markdown ֆայլերի վրա։ Կարող եք նաև տեղադրել տեքստ, ձեռքով ավելացնել հարցուպատասխաններ կամ ներբեռնել բովանդակություն ձեր կայքից։",
  "faq.q5.q": "Որքա՞ն է տևում կարգավորումը։",
  "faq.q5.a": "Օգտատերերի մեծ մասը գործարկում է նույն օրը։ Վերբեռնեք նյութերը, ձևավորեք վիջեթը, տեղադրեք մեկ տող կոդ՝ և պատրաստ է։",
  "faq.q6.q": "Կարո՞ղ եմ տեսնել՝ ինչ են հարցնում հաճախորդները։",
  "faq.q6.a": "Այո։ Բոլոր փաթեթներում հասանելի է զրույցների պատմությունը։ Basic և Pro փաթեթները նաև տալիս են վերլուծություն, հայտնի հարցերի հաշվետվություն և արտահանում։",
  "faq.q7.q": "Ի՞նչ լեզուներ է աջակցում։",
  "faq.q7.a": "Վիջեթի սեփական տեքստը՝ ողջույնը, հուշումը և առաջարկվող հարցերը, պատրաստ է 28 լեզվով՝ հայերեն, անգլերեն, ռուսերեն, իսպաներեն, ֆրանսերեն, գերմաներեն, իտալերեն, պորտուգալերեն, հոլանդերեն, լեհերեն, չեխերեն, հունգարերեն, ռումիներեն, հունարեն, ուկրաիներեն, թուրքերեն, արաբերեն, եբրայերեն, հինդի, թայերեն, վիետնամերեն, չինարեն, ճապոներեն, կորեերեն, դանիերեն, ֆիններեն, նորվեգերեն և շվեդերեն։ Ձերը ընտրում եք կարգավորումներում։ Զրույցները սահմանափակված չեն այդ ցանկով՝ չատբոթը կարող է պատասխանել նաև այլ լեզուներով։ Այն միշտ չէ, որ ինքնաբերաբար անցնում է մեկ բառից, բայց եթե խնդրեք պատասխանել որոշակի լեզվով՝ կաշխատի։ Վահանակը ներկայումս անգլերեն է։",
  "faq.q8.q": "Կարո՞ղ եմ չեղարկել ցանկացած պահի։",
  "faq.q8.a": "Այո։ Առանց պայմանագրի և չեղարկման վճարի։ Չեղարկեք անմիջապես վահանակից՝ երբ ցանկանաք։",

  // CTA
  "cta.title1": "Մի կորցրեք հաճախորդներ։",
  "cta.title2": "Սկսեք 14 օր անվճար։",
  "cta.subtitle": "Առանց բանկային քարտի։ Փաթեթներ՝ $7.99/ամսից։",
  "cta.primary": "Փորձել անվճար",
  "cta.primaryLoggedIn": "Բացել վահանակը",
  "cta.secondary": "Համեմատել փաթեթները",

  // Footer
  "footer.tagline": "AI սպասարկում ժամանակակից բիզնեսների համար։",
  "footer.product": "Ապրանք",
  "footer.integrations": "Ինտեգրումներ",
  "footer.legal": "Իրավական",
  "footer.account": "Հաշիվ",
  "footer.security": "Անվտանգություն",
  "footer.privacy": "Գաղտնիության քաղաքականություն",
  "footer.terms": "Օգտագործման պայմաններ",
  "footer.refund": "Վերադարձի քաղաքականություն",
  "footer.cookie": "Cookie քաղաքականություն",
  "footer.login": "Մուտք",
  "footer.signup": "Գրանցվել",
  "footer.dashboardLink": "Վահանակ",
  "footer.support": "Աջակցություն",
  "footer.rights": "Բոլոր իրավունքները պաշտպանված են։",
  "footer.ctaText": "Փորձեք PaperBrainz-ն անվճար {days} օր՝ առանց բանկային քարտի։ Փաթեթները՝ ${price}/ամսից։",

  // Language switcher
  "lang.en": "EN",
  "lang.hy": "ՀԱՅ",
};

export const translations = { en, hy };
export const SUPPORTED_LANGS = ["en", "hy"];
export const DEFAULT_LANG = "en";
